import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const products = [
  {
    id: 'check',
    name: 'BaciCheck',
    tagline: 'Accessibility review software for teams that need clear fixes fast.',
    description: 'Scan live websites against WCAG guidance, prioritize issues, and keep remediation work grounded in practical next steps.',
    features: ['WCAG audits', 'Priority-ranked issues', 'Fix guidance', 'Rescan tracking'],
    url: 'https://bacicheck.bacimo.net',
    icon: '♿',
  },
  {
    id: 'mocr',
    name: 'BacimOCR',
    tagline: 'Document extraction built for invoice, form, and operations workflows.',
    description: 'Process business documents with reliable OCR and structured outputs, whether teams need a hosted service or infrastructure-first deployment.',
    features: ['Structured extraction', 'Multi-language support', 'Hosted or private', 'API-ready output'],
    url: 'https://bacimocr.bacimo.net',
    icon: '📄',
  },
];

const inDevelopment = [
  {
    id: 'scout',
    name: 'BaciScout',
    tagline: 'EU Tender Intelligence',
    description: 'Tender discovery and qualification software for teams navigating EU procurement with more context and less manual filtering.',
    features: ['Tender discovery', 'Fit analysis', 'Competitive context'],
    icon: '🎯',
  },
];

const labsProjects = [
  {
    name: 'Laterloom',
    description: 'A quiet lab concept exploring delayed publishing, reminders, and message timing for lightweight digital workflows.',
    status: 'Lab',
  },
  {
    name: 'DreamsOut',
    description: 'A small experimental concept around capturing loose ideas and turning them into structured prompts or creative starting points.',
    status: 'Lab',
  },
];

const Home = () => {
  return (
    <main id="main-content" className="home" role="main">
      <section className="hero" aria-labelledby="hero-heading">
        <div className="hero-glow" aria-hidden="true"></div>
        <div className="hero-content container">
          <div className="hero-brand">
            <img 
              src={`${process.env.PUBLIC_URL}/logo192.png`}
              alt=""
              className="hero-logo"
              aria-hidden="true"
            />
          </div>
          
          <h1 id="hero-heading" className="hero-headline">
            Practical AI products for real business work.
          </h1>

          <p className="hero-subheadline">
            BACIMO is a small AI product studio focused on working software: tools for accessibility, documents, and operational workflows that teams can actually use.
          </p>

          <div className="hero-cta">
            <a href="#products" className="btn btn-primary">
              Explore live products
            </a>
            <Link to="/contact" className="btn btn-secondary hero-secondary-cta">
              Contact for partnerships, pilots, or feedback
            </Link>
          </div>
        </div>
        
        <div className="hero-visual" aria-hidden="true">
          <div className="orbital-ring ring-1"></div>
          <div className="orbital-ring ring-2"></div>
          <div className="orbital-ring ring-3"></div>
        </div>
      </section>

      <section id="products" className="products-section" aria-labelledby="products-heading">
        <div className="container">
          <header className="section-header">
            <h2 id="products-heading" className="section-title">Live products</h2>
            <p className="section-subtitle">Two focused BACIMO products available now for registered users.</p>
          </header>

          <div className="products-grid" role="list" aria-label="Live products list">
            {products.map((product, index) => (
              <article 
                key={product.id} 
                className={`product-card product-${product.id}`}
                style={{ animationDelay: `${index * 100}ms` }}
                role="listitem"
              >
                <div className="product-header">
                  <span className="product-icon" aria-hidden="true">{product.icon}</span>
                  <div>
                    <div className="product-badges" aria-label={`${product.name} availability`}>
                      <span className="product-badge">Live</span>
                      <span className="product-availability">Available for registered users</span>
                    </div>
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-tagline">{product.tagline}</p>
                  </div>
                </div>
                
                <p className="product-description">{product.description}</p>
                
                <ul className="product-features" aria-label={`${product.name} features`}>
                  {product.features.map((feature, i) => (
                    <li key={i}>
                      <span className="feature-check" aria-hidden="true">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="product-footer">
                  <a 
                    href={product.url} 
                    className="btn btn-primary"
                    aria-label={`Launch ${product.name}`}
                  >
                    Launch App <span aria-hidden="true">→</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="credibility-section" aria-labelledby="credibility-heading">
        <div className="container">
          <header className="section-header credibility-header">
            <h2 id="credibility-heading" className="section-title">Built for steady delivery</h2>
            <p className="section-subtitle">
              BACIMO keeps the story grounded in shipped work, clear operating principles, and practical product positioning.
            </p>
          </header>

          <dl className="credibility-strip" aria-label="BACIMO product principles">
            <div className="credibility-item">
              <dt>Working software</dt>
              <dd>Live products stay front and center with clear use cases and deliberate scope.</dd>
            </div>
            <div className="credibility-item">
              <dt>Accessible by design</dt>
              <dd>Accessibility and usability are treated as product quality, not cleanup work.</dd>
            </div>
            <div className="credibility-item">
              <dt>Built for real workflows</dt>
              <dd>Every BACIMO product is framed around everyday business tasks teams already need to complete.</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="behind-section" aria-labelledby="behind-heading">
        <div className="container">
          <div className="behind-grid">
            <div className="behind-content">
              <span className="behind-label">Roadmap</span>
              <h2 id="behind-heading" className="behind-title">
                In development
              </h2>
              <p className="behind-text">
                BACIMO keeps the roadmap secondary to the live products. Current development work stays close to operational research and practical business use cases.
              </p>
              <Link to="/contact" className="btn btn-secondary">
                Partnerships & feedback <span aria-hidden="true">→</span>
              </Link>
            </div>
            <div className="behind-visual">
              <div className="development-list" role="list" aria-label="In development products list">
                {inDevelopment.map((product) => (
                  <article key={product.id} className="development-card" role="listitem">
                    <div className="product-header">
                      <span className="product-icon" aria-hidden="true">{product.icon}</span>
                      <div>
                        <span className="product-badge product-badge-secondary">In development</span>
                        <h3 className="product-name">{product.name}</h3>
                        <p className="product-tagline">{product.tagline}</p>
                      </div>
                    </div>
                    <p className="product-description development-description">{product.description}</p>
                    <ul className="product-features development-features" aria-label={`${product.name} focus areas`}>
                      {product.features.map((feature) => (
                        <li key={feature}>
                          <span className="feature-check" aria-hidden="true">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="labs-section" aria-labelledby="labs-heading">
        <div className="container">
          <header className="section-header">
            <h2 id="labs-heading" className="section-title">Labs</h2>
            <p className="section-subtitle">Small experiments kept clearly behind the live product line.</p>
          </header>
          
          <div className="labs-grid" role="list" aria-label="Labs preview list">
            {labsProjects.map((project) => (
              <article 
                key={project.name} 
                className="lab-card"
                role="listitem"
              >
                <div className="lab-header">
                  <h3 className="lab-name">{project.name}</h3>
                  <span 
                    className="lab-status"
                    aria-label={`Status: ${project.status}`}
                  >
                    {project.status}
                  </span>
                </div>
                <p className="lab-description">{project.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section" aria-labelledby="about-heading">
        <div className="container">
          <div className="about-shell">
            <div className="about-intro">
              <span className="behind-label">About</span>
              <h2 id="about-heading" className="section-title">About BACIMO</h2>
              <p className="about-lead">
                BACIMO is a small AI product studio building focused software for accessibility, document handling, and operational workflows.
              </p>
            </div>

            <div className="about-grid">
              <article className="about-card">
                <h3>Product-first positioning</h3>
                <p>
                  The public story stays centered on live products, practical use cases, and careful roadmap discipline instead of broad claims.
                </p>
              </article>
              <article className="about-card">
                <h3>Recruiter-safe signal</h3>
                <p>
                  The site presents BACIMO as a serious working portfolio: shipped tools, credible experiments, and measured next-stage development.
                </p>
              </article>
              <article className="about-card">
                <h3>Selective collaboration</h3>
                <p>
                  Partnerships, pilots, and product feedback are welcome when they align with useful business workflows and clear execution paths.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-cta-section" aria-labelledby="contact-cta-heading">
        <div className="container">
          <div className="contact-cta-card">
            <span className="behind-label">Contact</span>
            <h2 id="contact-cta-heading" className="section-title">Partnerships, pilots, and feedback</h2>
            <p className="contact-cta-text">
              If you want to discuss a pilot, a partnership, or product feedback, BACIMO keeps the contact path direct and focused.
            </p>
            <div className="contact-cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Start a conversation
              </Link>
              <a href="mailto:info@bacimo.net" className="btn btn-secondary">
                Email info@bacimo.net
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
